/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { useLocation } from 'js/lib/useRouter';

import { Hidden, Typography2 } from '@coursera/cds-core';

import useIsOnLocaleSubfolderRoute from 'bundles/internationalization-lib/hooks/useIsOnLocaleSubfolderRoute';
import SearchResultsHeader from 'bundles/search-common/components/SearchResultsHeader';
import SortByIndex from 'bundles/search-common/components/SortByIndex';
import InlineSearchFilters from 'bundles/search-common/components/filters/InlineSearchFilters';
import SearchFilters from 'bundles/search-common/components/filters/SearchFilters';
import useFilterListOrder from 'bundles/search-common/components/filters/useFilterListOrder';
import { SEARCH_FILTERS_INDEX_ID, SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { DEFAULT_SEARCH_FILTERS_SORTED_BY_USAGE } from 'bundles/search-common/constants/filter';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type { SearchPageEventingData } from 'bundles/search-common/types';
import { inferQueryFromParams } from 'bundles/search-common/utils/eventingUtils';
import { getShouldUseRemoveFilterHack } from 'bundles/search-common/utils/experimentUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import { createFilterKeyToOptionsMap, scrollUpIfNecessary } from 'bundles/search-common/utils/utils';
import { styles as SearchPageBodyStyles } from 'bundles/search-v2/components/SearchPageBody';

import _t from 'i18n!nls/search';

type Props = {
  isMobile: boolean;
  isMobileOrTabletView: boolean;
  optimisticActiveFilterCount: number;
  isSEOEntityPage: boolean;
  showNoResultsRecommendations: boolean;
  eventingData: SearchPageEventingData;
  headerIsScrolledUp: boolean;
  paginationType: 'infinite' | 'control';
};

const styles = {
  stickyContainer: css`
    height: 100%;
    width: 100%;
    min-height: 1000px;
    min-width: 50px;
    align-self: flex-start;
    padding-right: 16px; /* give space for auto-hide scrollbar */
  `,
  stickyFilterWrapper: css`
    position: sticky;
    top: 120px;
    padding-bottom: 32px;
  `,
  /* because the header changes height when scrolling up */
  stickyFilterWrapperWhenPageIsScrolled: css`
    top: 80px;
  `,
  scrollableFilters: css`
    height: calc(100vh - 180px); /* viewport height minus full heading height plus padding */
    overflow-y: auto;
    visibility: hidden; /* auto-hide scrollbar for filters */
    padding-right: 8px;
    padding-left: 3px;
    transition: visibility 0.7s; /* scrollbar hide delay */

    /* the magic that shows the scrollbar on hover */
    &:hover,
    &:focus,
    .filterContent {
      visibility: visible;
    }
  `,
  scrollableFiltersWhenPageIsScrolled: css`
    height: calc(100vh - 145px); /* viewport height minus shrunk heading height plus padding */
  `,
};

function scrollUpOnFilterChange(disableScrollAnimation: boolean) {
  scrollUpIfNecessary({
    disableScrollAnimation,
    mustBeScrolledPastThisElementId: 'search-results-header-wrapper',
  });
}

const SearchPageFilters = ({
  isMobile,
  isMobileOrTabletView,
  optimisticActiveFilterCount,
  isSEOEntityPage,
  showNoResultsRecommendations,
  eventingData,
  headerIsScrolledUp,
  paginationType,
}: Props) => {
  const { query: queryParams } = useLocation();
  const { query: searchQueryFromParams } = queryParams;
  const isOnLocaleSubfolderRoute = useIsOnLocaleSubfolderRoute();
  const showSortByFilter = !isSEOEntityPage;
  const { getSearchResults, addFilters, removeFilters, clearFilters, setFiltersByFacet, setSortBy, isLoading } =
    useSearch();
  const isConsumerSearch = useIsConsumerSerp();
  const isEQP = useIsEqp();
  const removeFilterHack = (isConsumerSearch || isEQP) && getShouldUseRemoveFilterHack();
  const searchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const filterData = removeFilterHack
    ? getSearchResults(SEARCH_MAIN_INDEX_ID)
    : getSearchResults(SEARCH_FILTERS_INDEX_ID);
  const { facets, pagination, facetFilters, hitsDisplayedCount = 0, sortBy } = searchData || {};
  const allFilters = filterData?.facets;
  const totalHits = pagination?.totalElements || undefined;
  const activeFilters = useMemo(() => [...new Set(facetFilters?.flat())], [facetFilters]);
  const filterListOrder = useFilterListOrder(DEFAULT_SEARCH_FILTERS_SORTED_BY_USAGE);
  const filterKeyToOptionsMap = useMemo(
    () => createFilterKeyToOptionsMap(facets, facetFilters, filterListOrder, allFilters),
    [allFilters, facetFilters, facets, filterListOrder]
  );

  const shouldShowInfiniteStylePagination = paginationType === 'infinite';
  const shouldShowStickySidebarFilters =
    !showNoResultsRecommendations && shouldShowInfiniteStylePagination && !isMobileOrTabletView;

  const isOnEQPSubfolderRoute = isOnLocaleSubfolderRoute && isSEOEntityPage;

  return (
    <div role="region" aria-label={_t('Filters')}>
      <SearchResultsHeader
        searchQuery={inferQueryFromParams(searchQueryFromParams)}
        numberOfHits={totalHits || undefined}
        shouldHide={!isMobile}
        isLoading={isLoading && hitsDisplayedCount === 0}
        activeFilterCount={optimisticActiveFilterCount}
        lessenHeaderFocus={true}
        hideHits={true}
      />
      <div
        css={[
          SearchPageBodyStyles.header,
          isMobile && SearchPageBodyStyles.headerMobile,
          SearchPageBodyStyles.fullHeight,
        ]}
      >
        <div css={[SearchPageBodyStyles.mobileSortByLayoutShim, SearchPageBodyStyles.fullHeight]}>
          {/* where the mobile problem starts */}
          <Hidden smDown css={[shouldShowStickySidebarFilters && styles.stickyContainer]}>
            <div
              css={[
                shouldShowStickySidebarFilters && styles.stickyFilterWrapper,
                shouldShowStickySidebarFilters && headerIsScrolledUp && styles.stickyFilterWrapperWhenPageIsScrolled,
              ]}
            >
              {shouldShowStickySidebarFilters && (
                <Typography2 variant="subtitleLarge" component="h2" css={{ marginTop: '8px', marginBottom: '32px' }}>
                  {_t('Filter by')}
                </Typography2>
              )}
              <div
                css={[
                  shouldShowStickySidebarFilters && styles.scrollableFilters,
                  shouldShowStickySidebarFilters && headerIsScrolledUp && styles.scrollableFiltersWhenPageIsScrolled,
                ]}
              >
                <div className="filterContent">
                  {!showNoResultsRecommendations && !isMobile && shouldShowInfiniteStylePagination && (
                    <InlineSearchFilters
                      eventingData={eventingData}
                      shouldShowSectionTitle={!shouldShowInfiniteStylePagination}
                      onFilterValueChange={() => scrollUpOnFilterChange(true)}
                      isMobile={isMobile}
                    />
                  )}
                </div>
              </div>
            </div>
          </Hidden>
          {(!shouldShowInfiniteStylePagination || isMobile) && (
            <SearchFilters
              filters={filterKeyToOptionsMap}
              activeFilters={activeFilters}
              eventingData={eventingData}
              isMobile={isMobile}
              showingNoResults={!!showNoResultsRecommendations}
              addFilters={(filter: string) => addFilters(SEARCH_MAIN_INDEX_ID, [filter])}
              setFiltersByFacet={(facetName: string, newFilters: string[]) => {
                scrollUpOnFilterChange(false);
                setFiltersByFacet(SEARCH_MAIN_INDEX_ID, facetName, newFilters);
              }}
              removeFilters={(filter: string) => removeFilters(SEARCH_MAIN_INDEX_ID, [filter])}
              clearFilters={() => clearFilters(SEARCH_MAIN_INDEX_ID)}
              filtersListOrder={filterListOrder}
              shouldHideSkillsFilter={isOnEQPSubfolderRoute}
            />
          )}
        </div>
        {/* mobile sortby */}
        {isMobile && showSortByFilter && !showNoResultsRecommendations && (
          <div>
            <SortByIndex
              setSortBy={(sortByValue) => setSortBy(SEARCH_MAIN_INDEX_ID, sortByValue)}
              sortByValue={sortBy}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default SearchPageFilters;
